import { useMemo } from "react";
import {
    normalizeQuery,
    pickEmailKey,
    pickExpiresAt,
    pickNameKey,
    pickPlanCode,
    safeString,
} from "../utils/format";
import { pickListingsCount } from "../utils/user";
import { formatDateRu, getDaysLeft } from "../utils/date";

export type SortKey = "expiresAt" | "listingsCount" | "name" | "email";
export type SortDir = "asc" | "desc";

const EXPIRING_DAYS = 7;

type Args = {
    users: any[];
    search: string;
    sortKey: SortKey;
    sortDir: SortDir;
};

export const useDecoratedUsers = ({ users, search, sortKey, sortDir }: Args) => {
    return useMemo(() => {
        const list = Array.isArray(users) ? users : [];
        const q = normalizeQuery(search);

        const filtered = !q
            ? list
            : list.filter((u: any) => {
                const name = safeString(u?.name).toLowerCase();
                const phone = safeString(u?.phone);
                const email = safeString(u?.email).toLowerCase();
                const planCode = pickPlanCode(u).toLowerCase();

                return name.includes(q) || phone.includes(q) || email.includes(q) || planCode.includes(q);
            });

        const dir = sortDir === "asc" ? 1 : -1;

        const getExpiresTs = (u: any): number => {
            const iso = pickExpiresAt(u);
            if (!iso) return Number.POSITIVE_INFINITY;
            const ts = new Date(iso).getTime();
            return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
        };

        const sorted = [...filtered].sort((a, b) => {
            if (sortKey === "expiresAt") return (getExpiresTs(a) - getExpiresTs(b)) * dir;
            if (sortKey === "listingsCount") return ((pickListingsCount(a) ?? -1) - (pickListingsCount(b) ?? -1)) * dir;
            if (sortKey === "name") return pickNameKey(a).localeCompare(pickNameKey(b), "ru", { sensitivity: "base" }) * dir;
            return pickEmailKey(a).localeCompare(pickEmailKey(b), "ru", { sensitivity: "base" }) * dir;
        });

        return sorted.map((u: any) => {
            const expiresAt = pickExpiresAt(u);
            const planCode = pickPlanCode(u);

            const daysLeft = getDaysLeft(expiresAt);
            const isExpired = typeof daysLeft === "number" ? daysLeft < 0 : false;
            const isExpiringSoon = typeof daysLeft === "number" ? daysLeft >= 0 && daysLeft <= EXPIRING_DAYS : false;

            return {
                ...u,
                _ui: {
                    ...(u?._ui || {}),
                    expiresAt,
                    planCode,
                    listingsCount: pickListingsCount(u),
                    daysLeft,
                    isExpired,
                    isExpiringSoon,
                    expiresLabel: formatDateRu(expiresAt),
                },
            };
        });
    }, [users, search, sortKey, sortDir]);
};
